import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatMoneyCHF } from '../utils/priceUtils';
import {
  FaTimes, FaFileUpload, FaSpinner, FaCheck, FaExclamationTriangle,
  FaPercent, FaTag, FaCalendarAlt, FaInfoCircle, FaChevronRight, FaTrash,
} from 'react-icons/fa';
import {
  createDiscountImportPreview,
  updateDiscountImportProposals,
  commitDiscountImport,
  cancelDiscountImport,
} from '../services/apiService';

/**
 * DiscountImportModal — admin discount manager
 *
 * Three-step workflow:
 *   1. Upload + configure rule mode (file, mode, optional schedule/label)
 *   2. Preview matched/skipped rows, optionally edit per-row in 'perRow' mode
 *   3. Confirm & commit; show success/failure summary
 *
 * Backend: POST /discount-imports → PATCH /discount-imports/:id/proposals →
 *          POST /discount-imports/:id/commit. The commit step funnels every
 *          row through the existing applyDiscount() path used by the single
 *          and bulk-modal flows, so import-created discounts behave identically.
 */

const BUCKET_LABELS = {
  matched: { label: 'Ready to apply', tone: 'green' },
  inStore: { label: 'In-store codes (skipped)', tone: 'gray' },
  notFound: { label: 'Pharmacode not in catalog', tone: 'amber' },
  rxBlocked: { label: 'Prescription-only (skipped)', tone: 'amber' },
  lifecycleHidden: { label: 'Discontinued / pre-launch', tone: 'amber' },
  noBasePrice: { label: 'No base price', tone: 'amber' },
  invalidProposal: { label: 'Invalid proposal', tone: 'red' },
};

const TONE = {
  green: 'bg-green-50 text-green-700 border-green-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  gray:  'bg-gray-50 text-gray-600 border-gray-200',
  red:   'bg-red-50 text-red-700 border-red-200',
};

const DEFAULT_TIERS = [
  { maxMonths: 3,  percent: 50 },
  { maxMonths: 6,  percent: 30 },
  { maxMonths: 12, percent: 15 },
];

const DiscountImportModal = ({ isOpen, onClose, onSuccess }) => {
  // ── step machine ───────────────────────────────────────────────────
  const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'done'

  // ── upload step state ──────────────────────────────────────────────
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('flatPercent');
  const [percent, setPercent] = useState(30);
  const [chfOff, setChfOff] = useState(5);
  const [tiers, setTiers] = useState(DEFAULT_TIERS);
  const [globalStartsAt, setGlobalStartsAt] = useState('');
  const [globalEndsAt, setGlobalEndsAt] = useState('');
  const [globalLabel, setGlobalLabel] = useState('');
  const fileInputRef = useRef(null);

  // ── preview / commit state ─────────────────────────────────────────
  const [importDoc, setImportDoc] = useState(null);
  const [activeBucket, setActiveBucket] = useState('matched');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [editingRow, setEditingRow] = useState(null); // pharmacode being edited

  // ── reset whenever modal opens ─────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    setStep('upload');
    setFile(null);
    setMode('flatPercent');
    setPercent(30);
    setChfOff(5);
    setTiers(DEFAULT_TIERS);
    setGlobalStartsAt('');
    setGlobalEndsAt('');
    setGlobalLabel('');
    setImportDoc(null);
    setActiveBucket('matched');
    setBusy(false);
    setError(null);
    setEditingRow(null);
  }, [isOpen]);

  // ── helpers ────────────────────────────────────────────────────────
  const modeCfg = useMemo(() => {
    if (mode === 'flatPercent')    return { mode: 'flatPercent', percent: Number(percent) };
    if (mode === 'flatFixedOff')   return { mode: 'flatFixedOff', chfOff: Number(chfOff) };
    if (mode === 'tieredByExpiry') return { mode: 'tieredByExpiry', tiers: tiers.map((t) => ({ maxMonths: Number(t.maxMonths), percent: Number(t.percent) })) };
    return { mode: 'perRow' };
  }, [mode, percent, chfOff, tiers]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setError(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0] || null;
    if (f) setFile(f);
  };

  const submitUpload = useCallback(async () => {
    if (!file) { setError('Pick a file first'); return; }
    setBusy(true);
    setError(null);
    try {
      const opts = {};
      if (globalStartsAt) opts.globalStartsAt = new Date(globalStartsAt).toISOString();
      if (globalEndsAt)   opts.globalEndsAt   = new Date(globalEndsAt).toISOString();
      if (globalLabel)    opts.globalLabel    = globalLabel;
      const r = await createDiscountImportPreview(file, modeCfg, opts);
      setImportDoc(r.data);
      setStep('preview');
      // Default to "matched" bucket; fall back to the first non-empty bucket.
      const s = r.data?.summary || {};
      const firstNonEmpty = ['matched', 'invalidProposal', 'notFound', 'inStore', 'rxBlocked', 'lifecycleHidden', 'noBasePrice'].find((b) => (s[b] || 0) > 0) || 'matched';
      setActiveBucket(firstNonEmpty);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  }, [file, modeCfg, globalStartsAt, globalEndsAt, globalLabel]);

  const saveRowEdit = useCallback(async (row, draft) => {
    if (!importDoc) return;
    setBusy(true);
    setError(null);
    try {
      const r = await updateDiscountImportProposals(importDoc._id, [{
        pharmacode: row.pharmacode,
        rawCode: row.rawCode,
        kind: draft.kind,
        amount: Number(draft.amount),
      }]);
      setImportDoc(r.data);
      setEditingRow(null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  }, [importDoc]);

  const submitCommit = useCallback(async () => {
    if (!importDoc) return;
    setBusy(true);
    setError(null);
    try {
      const r = await commitDiscountImport(importDoc._id);
      setImportDoc(r.data);
      setStep('done');
      if (typeof onSuccess === 'function') onSuccess(r.data);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  }, [importDoc, onSuccess]);

  const submitCancel = useCallback(async () => {
    if (importDoc && importDoc.status === 'previewed') {
      try { await cancelDiscountImport(importDoc._id); } catch { /* best effort */ }
    }
    onClose();
  }, [importDoc, onClose]);

  const rowsInBucket = useMemo(() => {
    if (!importDoc) return [];
    return importDoc.rows.filter((r) => r.bucket === activeBucket);
  }, [importDoc, activeBucket]);

  const summary = importDoc?.summary || {};

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
        onClick={(e) => { if (e.target === e.currentTarget) submitCancel(); }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.97 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-red-50 to-amber-50">
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FaFileUpload className="text-red-600" />
                Import discounts from file
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Upload a Galexis Verfalldaten PDF · preview matches · commit
              </p>
            </div>
            <button
              onClick={submitCancel}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>

          {/* Step indicator */}
          <div className="px-5 py-3 flex items-center gap-2 text-xs text-gray-500 border-b border-gray-100 bg-gray-50/40">
            <Step n={1} label="Upload" active={step === 'upload'} done={step !== 'upload'} />
            <FaChevronRight className="text-gray-300 w-3 h-3" />
            <Step n={2} label="Preview" active={step === 'preview'} done={step === 'done'} />
            <FaChevronRight className="text-gray-300 w-3 h-3" />
            <Step n={3} label="Result" active={step === 'done'} done={false} />
          </div>

          {error && (
            <div className="px-5 py-3 bg-red-50 border-b border-red-200 text-red-700 text-sm flex items-center gap-2">
              <FaExclamationTriangle /> {error}
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            {step === 'upload' && (
              <UploadStep
                file={file}
                onFileChange={onFileChange}
                onDrop={onDrop}
                fileInputRef={fileInputRef}
                mode={mode}
                setMode={setMode}
                percent={percent}
                setPercent={setPercent}
                chfOff={chfOff}
                setChfOff={setChfOff}
                tiers={tiers}
                setTiers={setTiers}
                globalStartsAt={globalStartsAt}
                setGlobalStartsAt={setGlobalStartsAt}
                globalEndsAt={globalEndsAt}
                setGlobalEndsAt={setGlobalEndsAt}
                globalLabel={globalLabel}
                setGlobalLabel={setGlobalLabel}
              />
            )}

            {step === 'preview' && importDoc && (
              <PreviewStep
                importDoc={importDoc}
                summary={summary}
                activeBucket={activeBucket}
                setActiveBucket={setActiveBucket}
                rowsInBucket={rowsInBucket}
                editingRow={editingRow}
                setEditingRow={setEditingRow}
                onSaveRowEdit={saveRowEdit}
                busy={busy}
              />
            )}

            {step === 'done' && importDoc && (
              <DoneStep summary={summary} />
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 bg-gray-50">
            <div className="text-xs text-gray-500">
              {step === 'preview' && (
                <>
                  <span className="font-medium text-green-700">{summary.matched || 0}</span> ready to apply
                  {(summary.invalidProposal || 0) > 0 && (
                    <> · <span className="font-medium text-red-700">{summary.invalidProposal}</span> invalid</>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={submitCancel}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-white"
                disabled={busy}
              >
                {step === 'done' ? 'Close' : 'Cancel'}
              </button>
              {step === 'upload' && (
                <button
                  onClick={submitUpload}
                  disabled={!file || busy}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {busy ? <FaSpinner className="animate-spin" /> : <FaChevronRight />}
                  Parse & preview
                </button>
              )}
              {step === 'preview' && (
                <button
                  onClick={submitCommit}
                  disabled={busy || (summary.matched || 0) === 0}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {busy ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                  Apply {summary.matched || 0} discount{(summary.matched || 0) === 1 ? '' : 's'}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ── sub-components ───────────────────────────────────────────────────

const Step = ({ n, label, active, done }) => (
  <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
    active ? 'bg-red-100 text-red-700' : done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
  }`}>
    <span className={`w-4 h-4 rounded-full inline-flex items-center justify-center text-[10px] ${
      active ? 'bg-red-600 text-white' : done ? 'bg-green-600 text-white' : 'bg-gray-300 text-white'
    }`}>{done ? <FaCheck className="w-2 h-2" /> : n}</span>
    {label}
  </span>
);

const UploadStep = (props) => {
  const {
    file, onFileChange, onDrop, fileInputRef,
    mode, setMode, percent, setPercent, chfOff, setChfOff, tiers, setTiers,
    globalStartsAt, setGlobalStartsAt, globalEndsAt, setGlobalEndsAt, globalLabel, setGlobalLabel,
  } = props;

  return (
    <div className="p-6 space-y-6">
      {/* File picker */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-red-400 hover:bg-red-50/30 transition"
      >
        <FaFileUpload className="mx-auto text-3xl text-gray-400 mb-2" />
        {file ? (
          <div>
            <p className="text-sm font-medium text-gray-900">{file.name}</p>
            <p className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB · click to replace</p>
          </div>
        ) : (
          <div>
            <p className="text-sm font-medium text-gray-900">Drop a Verfalldaten PDF here</p>
            <p className="text-xs text-gray-500 mt-1">or click to browse</p>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          onChange={onFileChange}
          className="hidden"
        />
      </div>

      {/* Mode selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Discount rule</label>
        <div className="grid grid-cols-2 gap-2">
          <ModeOption value="flatPercent"    label="Flat percentage"           hint="One % applied to every matched row" current={mode} setMode={setMode} icon={<FaPercent />} />
          <ModeOption value="flatFixedOff"   label="Flat CHF off"              hint="Subtract a fixed amount from the price" current={mode} setMode={setMode} icon={<FaTag />} />
          <ModeOption value="tieredByExpiry" label="Tiered by months to expiry" hint="Bigger discount for sooner-expiring stock" current={mode} setMode={setMode} icon={<FaCalendarAlt />} />
          <ModeOption value="perRow"         label="Per-row (edit in preview)" hint="Set each row manually after parsing" current={mode} setMode={setMode} icon={<FaInfoCircle />} />
        </div>
      </div>

      {/* Mode-specific controls */}
      {mode === 'flatPercent' && (
        <NumberRow label="Percent off" value={percent} onChange={setPercent} suffix="%" hint="0–99" />
      )}
      {mode === 'flatFixedOff' && (
        <NumberRow label="CHF off" value={chfOff} onChange={setChfOff} prefix="CHF" hint="Subtracted from each row's price" />
      )}
      {mode === 'tieredByExpiry' && (
        <TierEditor tiers={tiers} setTiers={setTiers} />
      )}
      {mode === 'perRow' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
          <FaInfoCircle className="inline mr-2" />
          You'll be able to set the kind and amount for each row in the next step.
        </div>
      )}

      {/* Schedule (optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Active window (optional)</label>
        <div className="grid grid-cols-2 gap-3">
          <input
            type="date"
            value={globalStartsAt}
            onChange={(e) => setGlobalStartsAt(e.target.value)}
            placeholder="Starts at"
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
          />
          <input
            type="date"
            value={globalEndsAt}
            onChange={(e) => setGlobalEndsAt(e.target.value)}
            placeholder="Ends at"
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">Leave blank for "live immediately, no end date".</p>
      </div>

      {/* Label */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Label (optional)</label>
        <input
          type="text"
          value={globalLabel}
          onChange={(e) => setGlobalLabel(e.target.value)}
          placeholder="Auto-generated from filename if blank"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
        />
      </div>
    </div>
  );
};

const ModeOption = ({ value, label, hint, current, setMode, icon }) => (
  <button
    type="button"
    onClick={() => setMode(value)}
    className={`text-left p-3 rounded-lg border-2 transition ${
      current === value
        ? 'border-red-500 bg-red-50/50'
        : 'border-gray-200 hover:border-gray-300 bg-white'
    }`}
  >
    <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
      <span className={current === value ? 'text-red-600' : 'text-gray-400'}>{icon}</span>
      {label}
    </div>
    <p className="text-xs text-gray-500 mt-1 pl-6">{hint}</p>
  </button>
);

const NumberRow = ({ label, value, onChange, prefix, suffix, hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <div className="flex items-center gap-2">
      {prefix && <span className="text-sm text-gray-500">{prefix}</span>}
      <input
        type="number"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
      />
      {suffix && <span className="text-sm text-gray-500">{suffix}</span>}
      {hint && <span className="text-xs text-gray-400 ml-2">{hint}</span>}
    </div>
  </div>
);

const TierEditor = ({ tiers, setTiers }) => {
  const update = (i, patch) => {
    setTiers(tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  };
  const add = () => setTiers([...tiers, { maxMonths: 24, percent: 10 }]);
  const remove = (i) => setTiers(tiers.filter((_, idx) => idx !== i));

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Tiers (months remaining → percent off)</label>
      <div className="space-y-2">
        {tiers.map((t, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-12">If ≤</span>
            <input
              type="number"
              value={t.maxMonths}
              onChange={(e) => update(i, { maxMonths: e.target.value })}
              className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
            <span className="text-xs text-gray-500">months → apply</span>
            <input
              type="number"
              value={t.percent}
              onChange={(e) => update(i, { percent: e.target.value })}
              className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
            <span className="text-xs text-gray-500">%</span>
            <button
              type="button"
              onClick={() => remove(i)}
              className="ml-2 text-red-500 hover:text-red-700"
              aria-label="Remove tier"
            >
              <FaTrash className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          className="text-xs text-red-600 hover:text-red-700 font-medium"
        >
          + add tier
        </button>
      </div>
      <p className="text-xs text-gray-500 mt-2">Tiers are evaluated tightest-first; rows beyond the highest tier get no discount.</p>
    </div>
  );
};

const PreviewStep = ({ importDoc, summary, activeBucket, setActiveBucket, rowsInBucket, editingRow, setEditingRow, onSaveRowEdit, busy }) => {
  return (
    <div className="flex flex-col h-full">
      {/* Bucket tabs */}
      <div className="flex flex-wrap gap-2 px-5 py-3 bg-gray-50/60 border-b border-gray-200">
        {Object.entries(BUCKET_LABELS).map(([key, info]) => {
          const count = summary[key] || 0;
          if (count === 0 && key !== 'matched') return null;
          const isActive = activeBucket === key;
          return (
            <button
              key={key}
              onClick={() => setActiveBucket(key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                isActive ? TONE[info.tone] + ' ring-2 ring-offset-1 ring-red-300' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {info.label} <span className="ml-1 font-bold">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Row table */}
      <div className="flex-1 overflow-y-auto">
        {rowsInBucket.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-400">
            No rows in this bucket.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide sticky top-0">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Pharmacode</th>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-right px-4 py-2 font-medium">Base</th>
                <th className="text-right px-4 py-2 font-medium">PDF price</th>
                <th className="text-left px-4 py-2 font-medium">Expiry</th>
                <th className="text-left px-4 py-2 font-medium">Proposal</th>
                <th className="text-left px-4 py-2 font-medium">Existing</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rowsInBucket.map((row) => (
                <RowDisplay
                  key={row.rawCode}
                  row={row}
                  isEditing={editingRow === row.rawCode}
                  onEdit={() => setEditingRow(row.rawCode)}
                  onCancel={() => setEditingRow(null)}
                  onSave={(draft) => onSaveRowEdit(row, draft)}
                  isActive={activeBucket === 'matched' || activeBucket === 'invalidProposal'}
                  busy={busy}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const RowDisplay = ({ row, isEditing, onEdit, onCancel, onSave, isActive, busy }) => {
  const [draftKind, setDraftKind] = useState(row.proposal?.kind || 'percent');
  const [draftAmount, setDraftAmount] = useState(row.proposal?.amount ?? '');

  useEffect(() => {
    setDraftKind(row.proposal?.kind || 'percent');
    setDraftAmount(row.proposal?.amount ?? '');
  }, [row.proposal?.kind, row.proposal?.amount, isEditing]);

  const expiryStr = row.expiryDate
    ? new Date(row.expiryDate).toISOString().slice(0, 7)
    : '—';

  const proposalCell = isEditing ? (
    <div className="flex items-center gap-1.5">
      <select
        value={draftKind}
        onChange={(e) => setDraftKind(e.target.value)}
        className="text-xs px-1.5 py-1 border border-gray-300 rounded"
      >
        <option value="percent">%</option>
        <option value="fixed">CHF</option>
      </select>
      <input
        type="number"
        step="0.01"
        value={draftAmount}
        onChange={(e) => setDraftAmount(e.target.value)}
        className="w-20 text-xs px-1.5 py-1 border border-gray-300 rounded"
      />
      <button
        onClick={() => onSave({ kind: draftKind, amount: draftAmount })}
        disabled={busy}
        className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
      >Save</button>
      <button
        onClick={onCancel}
        className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
      >×</button>
    </div>
  ) : row.proposal && row.proposal.kind ? (
    <span className={row.proposalError ? 'text-red-600' : 'text-gray-700'}>
      {row.proposal.kind === 'percent'
        ? `−${row.proposal.amount}%`
        : `→ ${formatMoneyCHF(row.proposal.amount)}`}
      {row.proposalError && (
        <span className="block text-[10px] text-red-500">{row.proposalError}</span>
      )}
    </span>
  ) : (
    <span className="text-gray-400">—</span>
  );

  const existingCell = row.existingDiscount ? (
    <span className="inline-flex items-center gap-1 text-amber-700 text-xs">
      <FaExclamationTriangle className="w-3 h-3" />
      {row.existingDiscount.kind === 'percent'
        ? `−${row.existingDiscount.amount}%`
        : formatMoneyCHF(row.existingDiscount.amount)}
      <span className="text-gray-500">will be replaced</span>
    </span>
  ) : null;

  return (
    <tr className="hover:bg-gray-50/60">
      <td className="px-4 py-2 font-mono text-xs text-gray-700">{row.pharmacode ?? row.rawCode}</td>
      <td className="px-4 py-2 text-gray-900">{row.name}</td>
      <td className="px-4 py-2 text-right text-gray-700">{row.basePrice != null ? formatMoneyCHF(row.basePrice) : '—'}</td>
      <td className="px-4 py-2 text-right text-gray-500">{row.currentPrice != null ? formatMoneyCHF(row.currentPrice) : '—'}</td>
      <td className="px-4 py-2 text-gray-700">{expiryStr}</td>
      <td className="px-4 py-2">{proposalCell}</td>
      <td className="px-4 py-2">{existingCell}</td>
      <td className="px-4 py-2 text-right">
        {isActive && !isEditing && (
          <button
            onClick={onEdit}
            className="text-xs text-red-600 hover:text-red-700"
          >
            edit
          </button>
        )}
      </td>
    </tr>
  );
};

const DoneStep = ({ summary }) => (
  <div className="p-8 text-center space-y-4">
    <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
      <FaCheck className="text-green-600 text-2xl" />
    </div>
    <div>
      <h3 className="text-xl font-bold text-gray-900">Discount import complete</h3>
      <p className="text-sm text-gray-500 mt-1">
        <span className="font-semibold text-green-700">{summary.committedSuccess || 0}</span> discount{(summary.committedSuccess || 0) === 1 ? '' : 's'} applied
        {(summary.committedFailed || 0) > 0 && (
          <>{' · '}<span className="font-semibold text-red-700">{summary.committedFailed}</span> failed</>
        )}
      </p>
    </div>
    {(summary.committedFailed || 0) > 0 && (
      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 max-w-md mx-auto">
        Some rows failed to apply. Open this import from the discount manager's history to see the per-row errors.
      </div>
    )}
  </div>
);

export default DiscountImportModal;
